/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-magic-numbers */

import { ModuleStats, PracticeContentResponse, PracticeStats } from 'hooks/api/usePractice';
import { produce } from 'immer';
import { ModulePlayerStatsType } from 'pages/ModulePlayer/hooks/useModulePlayer';
import { AttemptInfo, QuestionType } from 'pages/TaskPlayer/types';
import { getValidatedDataForPracticeCueingEngine } from 'pages/TaskPlayer/validationUtil';
import { v4 as uuidv4 } from 'uuid';
import { StateCreator } from 'zustand';
import { AppState } from 'slices/appSlice';
import { StageState } from 'slices/stageSlice';
import { TestState } from 'slices/testSlice';

export const MAX_QUESTIONS_ALLOWED = 3;
const MIN_QUESTIONS_FOR_FAIL = 3;
const PASS_THRESHOLD = 2 / 3;
const getScoreRange = (
  questionStats: PracticeQuestionStats[],
  availableQuestions: QuestionType[],
) => {
  const totalAsked = questionStats.length;
  const remainingCount = Math.min(MAX_QUESTIONS_ALLOWED - totalAsked, availableQuestions.length);
  const correctCount = questionStats.filter((stat) => stat.isCorrect).length;
  return {
    minScore: correctCount / (totalAsked + remainingCount),
    maxScore: (correctCount + remainingCount) / (totalAsked + remainingCount),
    score: correctCount / totalAsked,
    remainingCount,
  };
};

const selectRandomQuestion = (questions: QuestionType[]) => {
  return questions[Math.floor(Math.random() * questions.length)];
};

const isCurrentSetOver = (
  questionStats: PracticeQuestionStats[],
  availableQuestions: QuestionType[],
) => {
  const { /* minScore, maxScore,  */ remainingCount, score } = getScoreRange(
    questionStats,
    availableQuestions,
  );
  return {
    currentSetOver:
      remainingCount === 0 /* || minScore >= PASS_THRESHOLD || maxScore < PASS_THRESHOLD */,
    score,
  };
};

const precompute = (getPracticeContentResponse: PracticeContentResponse) => {
  return getValidatedDataForPracticeCueingEngine(getPracticeContentResponse);
};

interface BasicQuestionStats {
  questionId: string;
  format: string;
  medium: string;
  seqNo: number;
  isCorrect: boolean;
  translationViewed: string[];
  type: number;
}

export interface PracticeQuestionStats extends BasicQuestionStats, AttemptInfo {}

export interface PracticeState {
  /** api response related state */
  getPracticeContentResponse?: PracticeContentResponse;

  /** stats related state */
  currentPracticeQuestionStat?: PracticeQuestionStats;
  skillWiseQuestionStats: { [skillId: string]: PracticeQuestionStats[] };
  currentSkillQuestionStats: PracticeQuestionStats[];
  skillWiseModuleStats: { [skillId: string]: ModuleStats[] };

  /** practice journey related state */
  showPracticeModules: boolean;
  practiceStatsAvailableFrom?: string;

  /** api execution related state */
  submitPracticeResultRetryCount: number;

  /** logic related state */
  currentPracticeSkillIndex: number;

  /** api response parsing related behaviour */
  initialisePractice: ({
    getPracticeContentResponse,
  }: {
    getPracticeContentResponse: PracticeContentResponse;
  }) => void;

  /** stats related behaviour */
  storeUserAttemptPractice: ({
    attemptInfo,
    hintUsed,
    isCorrect,
    medium,
  }: {
    attemptInfo: AttemptInfo;
    isCorrect: boolean;
    hintUsed: number;
    medium: string;
  }) => void;

  /** practice journey related behaviour */
  setShowPracticeModules: (showPracticeModules: boolean) => void;
  clearPracticeState: () => void;
  setPracticeStatsAvailableFrom: (source?: string) => void;

  /** api execution related behaviour */
  incrementSubmitPracticeResultRetryCount: () => void;
  getSubmitPracticeResultRetryCount: () => number;

  /** logic related behaviour */
  moveToNextSkill: () => void;
  processUserAnswerPractice: () => void;
  getAvailableQuestionsForPractice: () => QuestionType[];
  setModuleStats: (moduleStats: ModulePlayerStatsType) => void;
  getPracticeStats: () => PracticeStats | undefined;
}

const getInitialState = () => {
  return {
    /** api response related state */
    getPracticeContentResponse: undefined,

    /** stats related state */
    currentPracticeQuestionStat: undefined,
    skillWiseQuestionStats: {},
    currentSkillQuestionStats: [],
    skillWiseModuleStats: {},

    /** practice journey related state */
    showPracticeModules: false,
    practiceStatsAvailableFrom: undefined,

    /** api execution related state */
    submitPracticeResultRetryCount: 0,

    /** logic related state */
    question: undefined,
    currentPracticeSkillIndex: 0,
  };
};

const getInititalTranslationViewed = (
  currentQuestion: QuestionType | undefined,
  mediumPriority: string[],
) => {
  if (currentQuestion && mediumPriority) {
    // eslint-disable-next-line unicorn/prefer-array-find
    const availableLanguages = mediumPriority.filter(
      (medium) => currentQuestion.mediumMapping[medium],
    );
    return [availableLanguages[0]];
  }
  return [];
};

export const createPracticeSlice: StateCreator<
  TestState & StageState & PracticeState & AppState,
  [['zustand/devtools', never]],
  [],
  PracticeState
> = (set, get) => ({
  ...getInitialState(),

  /** api response parsing related behaviour */
  initialisePractice: ({
    getPracticeContentResponse,
  }: {
    getPracticeContentResponse: PracticeContentResponse;
  }) =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          const { content, errors, isValid } = precompute(getPracticeContentResponse);
          if (isValid) {
            state.warnings = errors;
            state.getPracticeContentResponse = { ...getPracticeContentResponse, content };
            state.currentPracticeSkillIndex = 0;
            state.question = selectRandomQuestion(content[0].questions);
            state.translationViewed = getInititalTranslationViewed(
              state.question,
              getPracticeContentResponse.mediumPriority,
            );
            state.skillWiseQuestionStats = {};
            state.currentSkillQuestionStats = [];
          } else {
            state.errors = errors;
          }
        }),
      false,
      'practiceSlice/initialisePractice',
    ),

  /** stats related behaviour */
  storeUserAttemptPractice: ({
    attemptInfo,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hintUsed,
    isCorrect,
    medium,
  }: {
    attemptInfo: AttemptInfo;
    isCorrect: boolean;
    hintUsed: number;
    medium: string;
  }) =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          if (!state.currentSkillQuestionStats) {
            state.currentSkillQuestionStats = [];
          }
          if (!state.currentPracticeQuestionStat) {
            state.currentPracticeQuestionStat = {} as PracticeQuestionStats;
          }
          const {
            currentSkillQuestionStats,
            currentPracticeQuestionStat,
            question,
            translationViewed,
          } = state;
          const { answer, startTime, endTime, duration } = attemptInfo;
          if (!question) {
            return;
          }

          currentPracticeQuestionStat.answer = answer;
          currentPracticeQuestionStat.medium = medium;
          currentPracticeQuestionStat.isCorrect = isCorrect;
          currentPracticeQuestionStat.duration = duration;
          currentPracticeQuestionStat.endTime = endTime;

          currentPracticeQuestionStat.questionId = question.id;
          currentPracticeQuestionStat.type = question.type;
          currentPracticeQuestionStat.format = question.format;
          currentPracticeQuestionStat.startTime = startTime;
          currentPracticeQuestionStat.seqNo = currentSkillQuestionStats.length + 1;
          currentPracticeQuestionStat.translationViewed = translationViewed;
          currentSkillQuestionStats.push(currentPracticeQuestionStat);
        }),
      false,
      'practiceSlice/storeUserAttemptPractice',
    ),

  /** practice journey related behaviour */
  setShowPracticeModules: (showPracticeModules: boolean) =>
    set({ showPracticeModules }, false, 'practiceSlice/setShowPracticeModules'),
  clearPracticeState: () => {
    get().clearSharedState();
    set({ ...getInitialState() }, false, 'practiceSlice/clearPracticeState');
  },
  setPracticeStatsAvailableFrom: (practiceStatsAvailableFrom?: string) =>
    set({ practiceStatsAvailableFrom }, false, 'practiceSlice/setPracticeStatsAvailableFrom'),

  /** api execution related behaviour */
  incrementSubmitPracticeResultRetryCount: () =>
    set((state) => ({
      submitPracticeResultRetryCount: state.submitPracticeResultRetryCount + 1,
    })),
  getSubmitPracticeResultRetryCount: () => get().submitPracticeResultRetryCount,

  /** logic related behaviour */
  moveToNextSkill: () =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          const { currentPracticeSkillIndex, getPracticeContentResponse } = get();
          if (!getPracticeContentResponse) return;
          const contentLength = getPracticeContentResponse?.content?.length ?? 0;
          if (currentPracticeSkillIndex === contentLength - 1) {
            state.practiceStatsAvailableFrom = 'lastSkill';
          } else if (currentPracticeSkillIndex < contentLength - 1) {
            state.currentPracticeSkillIndex = currentPracticeSkillIndex + 1;
            state.showPracticeModules = false;
            state.question = selectRandomQuestion(
              getPracticeContentResponse.content[currentPracticeSkillIndex + 1].questions,
            );
          }
        }),
      false,
      'practiceSlice/moveToNextSkill',
    ),
  processUserAnswerPractice: () =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState & StageState) => {
          const {
            currentSkillQuestionStats,
            currentPracticeSkillIndex,
            getPracticeContentResponse,
            getAvailableQuestionsForPractice,
          } = state;
          if (!getPracticeContentResponse) return;
          const { skillId, modules, questions } =
            getPracticeContentResponse.content[currentPracticeSkillIndex];
          const availableQuestions = getAvailableQuestionsForPractice();
          const { currentSetOver, score } = isCurrentSetOver(
            currentSkillQuestionStats,
            availableQuestions,
          );

          if (currentSetOver) {
            // eslint-disable-next-line unicorn/consistent-destructuring
            state.skillWiseQuestionStats[skillId] = [...currentSkillQuestionStats];
            state.currentSkillQuestionStats = [];
            if (
              score < PASS_THRESHOLD &&
              questions.length >= MIN_QUESTIONS_FOR_FAIL &&
              modules.length > 0
            ) {
              state.showPracticeModules = true;
              state.currentModuleIndex = 0;
            } else if (currentPracticeSkillIndex < getPracticeContentResponse.content.length - 1) {
              state.currentPracticeSkillIndex += 1;
              state.question = selectRandomQuestion(
                getPracticeContentResponse.content[currentPracticeSkillIndex + 1].questions,
              );
            } else {
              state.practiceStatsAvailableFrom = 'lastSkill';
            }
          } else {
            state.question = selectRandomQuestion(availableQuestions);
          }
          state.translationViewed = getInititalTranslationViewed(
            // eslint-disable-next-line unicorn/consistent-destructuring
            state.question,
            getPracticeContentResponse?.mediumPriority,
          );
          state.currentQuestionStat = undefined;
        }),
      false,
      'practiceSlice/processUserAnswerPractice',
    ),
  getAvailableQuestionsForPractice: () => {
    const { getPracticeContentResponse, currentPracticeSkillIndex } = get();
    if (getPracticeContentResponse) {
      const { questions } = getPracticeContentResponse.content[currentPracticeSkillIndex];
      const attemptedQuestionIds = new Set(
        [
          ...Object.values(get().skillWiseQuestionStats).flat(),
          ...get().currentSkillQuestionStats,
        ].map((stat) => stat.questionId),
      );

      return questions?.filter((q) => !attemptedQuestionIds.has(q.id)) || [];
    }
    return [];
  },
  setModuleStats: (moduleStats: ModulePlayerStatsType) =>
    set(
      (_state) =>
        produce(_state, (state: PracticeState) => {
          const { currentPracticeSkillIndex, getPracticeContentResponse } = state;
          if (getPracticeContentResponse) {
            const { skillId } = getPracticeContentResponse.content[currentPracticeSkillIndex];
            // eslint-disable-next-line unicorn/consistent-destructuring
            if (!state.skillWiseModuleStats[skillId]) state.skillWiseModuleStats[skillId] = [];
            // eslint-disable-next-line unicorn/consistent-destructuring
            state.skillWiseModuleStats[skillId] = [
              // eslint-disable-next-line unicorn/consistent-destructuring
              ...state.skillWiseModuleStats[skillId],
              {
                moduleId: moduleStats.moduleId,
                attemptId: moduleStats.attemptId,
                moduleType: moduleStats.moduleType,
                metadata: {
                  ...moduleStats.metadata,
                },
                moduleCategory: moduleStats.moduleCategory,
              },
            ];
          }
        }),
      false,
      'practiceSlice/setModuleStats',
    ),
  getPracticeStats: () => {
    const {
      currentMission,
      getPracticeContentResponse,
      skillWiseQuestionStats,
      skillWiseModuleStats,
    } = get();
    let finalResult;
    if (getPracticeContentResponse && currentMission) {
      finalResult = {
        missionId: currentMission.id,
        practiceAttemptNo: getPracticeContentResponse.practiceAttemptNo,
        skillStats: getPracticeContentResponse.content
          .filter((skill) => skillWiseQuestionStats[skill.skillId].length)
          .map((skill) => {
            return {
              skillId: skill.skillId,
              strategyId: skill.strategyId,
              questionStats: {
                attemptId: uuidv4(),
                stats: skillWiseQuestionStats[skill.skillId] || [],
              },
              moduleStats: skillWiseModuleStats[skill.skillId] || [],
            };
          }),
      };
    }
    return finalResult;
  },
});
